import React from "react";
import { twMerge } from "tailwind-merge";

export type BadgeVariant =
  | "black"
  | "blue-light"
  | "blue-vibrant"
  | "blue"
  | "brand"
  | "error"
  | "grayBlue"
  | "indigo-vibrant"
  | "indigo"
  | "orange"
  | "pink-vibrant"
  | "pink"
  | "primary"
  | "purple"
  | "red-vibrant"
  | "secondary"
  | "success-vibrant"
  | "success"
  | "warning-vibrant"
  | "warning";

type Size = "large" | "medium" | "small";

type BadgeProps = {
  children: React.ReactNode;
  className?: string;
  isFilled?: boolean;
  isRounded?: boolean;
  size?: Size;
  variant?: BadgeVariant;
};

// Props for the Image component
type ImageProps = {
  alt?: string;
  className?: string;
  src: string;
};

// Props for the Icon component
type IconProps = {
  children: React.ReactNode;
  className?: string;
};

const badgeVariants = {
  base: "box-border font-medium border",
  filled: {
    black:
      "bg-utility-black-bg border-utility-black-border text-utility-black-text",
    blue: "bg-utility-blue-50 border-utility-blue-200 text-utility-blue-700",
    "blue-light":
      "bg-utility-blue-light-50 border-utility-blue-light-200 text-utility-blue-light-700",
    "blue-vibrant": "bg-utility-blue-600 border-utility-blue-800 text-white",
    brand:
      "bg-utility-brand-50 border-utility-brand-200 text-utility-brand-700",
    error:
      "bg-utility-error-50 border-utility-error-200 text-utility-error-700",
    grayBlue:
      "bg-utility-gray-blue-50 border-utility-gray-blue-200 text-utility-gray-blue-700",
    indigo:
      "bg-utility-indigo-50 border-utility-indigo-200 text-utility-indigo-700",
    "indigo-vibrant":
      "bg-utility-indigo-600 border-utility-indigo-800 text-white",
    orange:
      "bg-utility-orange-50 border-utility-orange-200 text-utility-orange-700",
    pink: "bg-utility-pink-50 border-utility-pink-200 text-utility-pink-700",
    "pink-vibrant":
      "bg-utility-pink-vibrant-500 border-utility-pink-vibrant-800 text-white",
    primary: "bg-utility-gray-50 border-utility-gray-200 text-utility-gray-700",
    purple:
      "bg-utility-purple-50 border-utility-purple-200 text-utility-purple-700",
    "red-vibrant":
      "bg-utility-error-vibrant-600 border-utility-error-vibrant-700 text-white",
    secondary:
      "border-border-primary text-text-secondary border-[1.5px] bg-bg-primary",
    success:
      "bg-utility-success-50 border-utility-success-200 text-utility-success-700",
    "success-vibrant":
      "bg-utility-success-vibrant-600 border-utility-success-vibrant-700 text-white",
    warning:
      "bg-utility-warning-50 border-utility-warning-200 text-utility-warning-700",
    "warning-vibrant":
      "bg-utility-warning-vibrant-400 border-utility-warning-vibrant-600 text-white",
  },
  outline: {
    black: "border-utility-black-border text-utility-black-text border-[1.5px]",
    blue: "border-utility-blue-600 text-utility-blue-700 border-[1.5px]",
    "blue-light":
      "border-utility-blue-light-600 text-utility-blue-light-700 border-[1.5px]",
    "blue-vibrant": "border-utility-blue-800 text-white border-[1.5px]",
    brand: "border-utility-brand-600 text-utility-brand-700 border-[1.5px]",
    error: "border-utility-error-600 text-utility-error-700 border-[1.5px]",
    grayBlue:
      "border-utility-gray-blue-600 text-utility-gray-blue-700 border-[1.5px]",
    indigo: "border-utility-indigo-600 text-utility-indigo-700 border-[1.5px]",
    "indigo-vibrant":
      "border-utility-indigo-800 text-utility-indigo-700 border-[1.5px]",
    orange: "border-utility-orange-600 text-utility-orange-700 border-[1.5px]",
    pink: "border-utility-pink-600 text-utility-pink-700 border-[1.5px]",
    "pink-vibrant": "border-utility-pink-vibrant-800 text-white border-[1.5px]",
    primary: "border-utility-gray-600 text-utility-gray-700 border-[1.5px]",
    purple: "border-utility-purple-600 text-utility-purple-700 border-[1.5px]",
    "red-vibrant": "border-utility-error-vibrant-700 text-white border-[1.5px]",
    secondary: "border-border-primary text-text-secondary border-[1.5px]",
    success:
      "border-utility-success-600 text-utility-success-700 border-[1.5px]",
    "success-vibrant":
      "border-utility-success-vibrant-700 text-white border-[1.5px]",
    warning:
      "border-utility-warning-600 text-utility-warning-700 border-[1.5px]",
    "warning-vibrant":
      "border-utility-warning-vibrant-600 text-white border-[1.5px]",
  },
};

const badgeSizes = {
  large: "min-h-[28px] py-xs text-sm",
  medium: "min-h-[24px] py-xxs text-sm",
  small: "min-h-[22px] py-xxs text-xs",
};

const badgeSpacing = {
  rounded: {
    large: {
      noIcon: "px-3",
      withIcon: {
        left: "pl-[10px] pr-3",
        right: "pl-3 pr-[10px]",
      },
    },
    medium: {
      noIcon: "px-[10px]",
      withIcon: {
        left: "pl-2 pr-[10px]",
        right: "pl-[10px] pr-2",
      },
    },
    small: {
      noIcon: "px-2",
      withIcon: {
        left: "pl-1.5 pr-2",
        right: "pl-2 pr-1.5",
      },
    },
  },
  squared: {
    large: {
      noIcon: "px-[10px]",
      withIcon: {
        left: "pl-2 pr-[10px]",
        right: "pl-[10px] pr-2",
      },
    },
    medium: {
      noIcon: "px-2",
      withIcon: {
        left: "pl-1.5 pr-2",
        right: "pl-2 pr-1.5",
      },
    },
    small: {
      noIcon: "px-1.5",
      withIcon: {
        left: "pl-1 pr-1.5",
        right: "pl-1.5 pr-1",
      },
    },
  },
};

function badgeStyle({
  isFilled = true,
  isRounded = false,
  size = "medium",
  variant = "primary",
}: Pick<BadgeProps, "isFilled" | "isRounded" | "size" | "variant">) {
  const variantStyle = isFilled
    ? badgeVariants.filled[variant]
    : badgeVariants.outline[variant];
  const roundedStyle = isRounded ? "rounded-xl" : "rounded-sm";

  return twMerge(
    badgeVariants.base,
    badgeSizes[size],
    variantStyle,
    roundedStyle,
  );
}

function Badge({
  children,
  className,
  isFilled = true,
  isRounded = false,
  size = "medium",
  variant = "primary",
}: BadgeProps) {
  const shapeType = isRounded ? "rounded" : "squared";
  const containerClass =
    "flex items-center justify-center gap-1 min-w-fit text-center";

  const detectIconPosition = () => {
    const childrenArray = React.Children.toArray(children);

    if (childrenArray.length <= 1) return;
    const isIcon = (el: React.ReactNode) =>
      React.isValidElement(el) &&
      [BadgeIcon, BadgeImage].includes(
        el.type as typeof BadgeIcon | typeof BadgeImage,
      );

    return isIcon(childrenArray[0])
      ? "left"
      : isIcon(childrenArray.at(-1))
        ? "right"
        : undefined;
  };

  const iconPosition = detectIconPosition();
  const hasIcon = iconPosition !== undefined;

  const paddingClass = hasIcon
    ? badgeSpacing[shapeType][size].withIcon[iconPosition]
    : badgeSpacing[shapeType][size].noIcon;

  return (
    <span
      className={twMerge(
        badgeStyle({ isFilled, isRounded, size, variant }),
        containerClass,
        paddingClass,
        className,
      )}
    >
      {children}
    </span>
  );
}

function BadgeImage({ alt = "", className, src }: ImageProps) {
  return (
    <img
      alt={alt}
      className={twMerge(
        "size-3 object-cover border-none rounded-full",
        className,
      )}
      src={src}
    />
  );
}

function BadgeIcon({ children, className }: IconProps) {
  return (
    <span
      className={twMerge("size-3 flex items-center justify-center", className)}
    >
      {children}
    </span>
  );
}

Badge.Image = BadgeImage;
Badge.Icon = BadgeIcon;

export { Badge };
